import React, { useEffect, useMemo } from 'react';
import { useFetchWishList } from './use-fetch-wish-list';
import Fuse from 'fuse.js';
import { useSelector } from 'react-redux';
import { WishListItem } from 'tradera/components/wish-list-item/wish-list-item';
import { useInView } from 'react-intersection-observer';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import HeartFilledIcon from '@tradera/blueprint/theme/icons2/heart-fill.svg?jsx';
import styles from './wish-list-side-sheet.module.scss';
import { Trans, useTranslator } from 'tradera/lang/translate';
import { WishListEmpty } from './../start-page/wish-list/wish-list-empty';

export const WishListItems = ({
    filter,
    searchTerm,
    setShowMayBeMoreResultsText
}) => {
    const { t } = useTranslator();

    const { fetchNextPage, page, isFetching, wishListItems, totalCount } =
        useFetchWishList(filter);

    const wishListItemIds = useSelector((state) => state.items.wishListItemIds);

    const { ref: loadingMoreRef, inView } = useInView();

    const hasSearchTerm = searchTerm.trim() !== '';
    const isAllItemsFetched = wishListItems.length === totalCount;

    useEffect(() => {
        if (hasSearchTerm || isFetching || !inView || isAllItemsFetched) return;

        fetchNextPage();
    }, [fetchNextPage, inView, isFetching, hasSearchTerm, isAllItemsFetched]);

    useEffect(() => {
        if (!hasSearchTerm || isAllItemsFetched)
            setShowMayBeMoreResultsText(false);

        const timeoutCheck = setTimeout(() => {
            if (!hasSearchTerm || isAllItemsFetched)
                setShowMayBeMoreResultsText(false);
            else if (!isAllItemsFetched) setShowMayBeMoreResultsText(true);
        }, 1000);

        return () => clearTimeout(timeoutCheck);
    }, [hasSearchTerm, setShowMayBeMoreResultsText, isAllItemsFetched]);

    const visibleWishListItems = useMemo(() => {
        if (!hasSearchTerm) return wishListItems;

        const fuseOptions = {
            ignoreLocation: true,
            threshold: 0.4,
            keys: ['shortDescription', 'sellerAlias']
        };
        const fuse = new Fuse(wishListItems, fuseOptions);

        return fuse.search(searchTerm).map((results) => results.item);
    }, [hasSearchTerm, searchTerm, wishListItems]);

    return (
        <>
            {!isFetching && wishListItems.length === 0 && (
                <div className="d-flex flex-column align-items-center mx-2 flex-grow-1 justify-content-center">
                    {filter === 'all' ? (
                        <>
                            <WishListEmpty className="mb-3" width={120} />
                            <h4 className="mb-0 text-center">
                                {t('wishlist_sidesheet_empty_title')}
                            </h4>
                            <p className="text-center">
                                <Trans
                                    i18nKey="wishlist_sidesheet_empty_content_Trans"
                                    components={{
                                        icon: (
                                            <HeartFilledIcon
                                                className={`icon icon-md icon-danger ${styles.heart}`}
                                            />
                                        )
                                    }}
                                />
                            </p>
                        </>
                    ) : (
                        <h4 className="text-center">
                            {t('wishlist_sidesheet_no_items_with_filter')}
                        </h4>
                    )}
                </div>
            )}
            {isFetching && page === 1 ? (
                <LoadingIcon className="icon icon-xl icon-spin m-auto" />
            ) : (
                <ul className="plain-list">
                    {visibleWishListItems?.map((item) => {
                        const existsInState = wishListItemIds.includes(
                            item.itemId
                        );

                        return (
                            <li key={item.itemId} className="border-bottom">
                                <WishListItem
                                    item={item}
                                    existsInState={existsInState}
                                    isMobile
                                />
                            </li>
                        );
                    })}
                    {isFetching && page > 1 && (
                        <div className="text-center">
                            <LoadingIcon className="icon icon-lg icon-spin m-auto" />
                        </div>
                    )}
                    {!isFetching && <div ref={loadingMoreRef} />}
                </ul>
            )}
        </>
    );
};
